import React, { useState } from 'react';
import { ArrowRight, Phone, ShieldCheck, Droplets, Sun, Sparkles, Wrench } from 'lucide-react';
import { services, businessInfo } from '../data/mock';
import { BookingFlow } from './booking/BookingFlow';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { AddonHints } from './AddonHints';
import { useSquareServices, findService } from '../hooks/useBookingData';

const CERAMIC_PERKS = [
  { Icon: Wrench, label: '1-step paint correction included', highlight: true },
  { Icon: ShieldCheck, label: 'Multi-year protection' },
  { Icon: Droplets, label: 'Hydrophobic finish' },
  { Icon: Sun, label: 'UV & oxidation defense' },
  { Icon: Sparkles, label: 'Mirror-like gloss' },
];

const FeaturedCard = ({ service, squareService, onBook }) => (
  <div
    data-testid={`service-card-featured-${service.id}`}
    className="group relative bg-neutral-950 rounded-sm overflow-hidden shadow-2xl shadow-neutral-900/20"
  >
    <div className="absolute top-4 left-4 z-10 bg-red-600 text-white font-display uppercase tracking-widest text-xs font-bold px-3 py-1.5 rounded-sm">
      Limited Special
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
      <div className="relative h-80 md:h-auto md:min-h-[460px] overflow-hidden">
        <BeforeAfterSlider before={service.beforeImage} after={service.afterImage} alt={service.name} />
      </div>
      <div className="p-8 md:p-12 flex flex-col justify-center border-t-4 md:border-t-0 md:border-l-4 border-red-600">
        <div className="font-display italic font-extrabold text-6xl md:text-7xl text-red-600 leading-none mb-2" data-testid="featured-price">
          {service.price}
        </div>
        <h3 className="font-display uppercase text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
          {service.name}
        </h3>
        <p className="text-white/75 text-base leading-relaxed mb-6">{service.description}</p>
        <AddonHints service={squareService} dark testId={`addon-hints-featured-${service.id}`} />
        <ul className="grid grid-cols-2 gap-3 mb-8">
          {CERAMIC_PERKS.map(({ Icon, label, highlight }) => (
            <li
              key={label}
              data-testid={highlight ? 'perk-paint-correction' : undefined}
              className={highlight
                ? 'col-span-2 flex items-center gap-2 text-white font-semibold text-sm bg-red-600/15 border border-red-600/50 rounded-sm px-3 py-2'
                : 'flex items-center gap-2 text-white/90 text-sm'}
            >
              <Icon className="w-4 h-4 text-red-500 shrink-0" />
              {label}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onBook(service.bookingName)}
            data-testid="featured-book-online-btn"
            className="group inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-display uppercase tracking-wider text-lg font-bold px-8 py-3.5 rounded-sm hover:-translate-y-0.5"
          >
            <span>Book Online</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <a
            href={`tel:${businessInfo.phone}`}
            data-testid="featured-call-now-btn"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-neutral-100 text-neutral-950 font-display uppercase tracking-wider text-lg font-bold px-8 py-3.5 rounded-sm hover:-translate-y-0.5"
          >
            <Phone className="w-5 h-5 text-red-600" />
            <span>Call Now</span>
          </a>
        </div>
        <p className="text-white/50 text-xs mt-3">Book online with a $50 deposit, or call {businessInfo.phone} with questions.</p>
      </div>
    </div>
  </div>
);

const ServiceCard = ({ service, squareService, onBook }) => (
  <div
    data-testid={`service-card-${service.id}`}
    className="group bg-white border border-neutral-200 hover:border-red-600 rounded-sm overflow-hidden shadow-sm hover:shadow-xl hover:shadow-neutral-900/10 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 flex flex-col"
  >
    <div className="h-40 overflow-hidden bg-neutral-100">
      <img
        src={service.image}
        alt={service.name}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-display uppercase text-xl font-bold text-neutral-950 mb-2">{service.name}</h3>
      <p className="text-neutral-600 text-sm leading-relaxed mb-4 flex-1">{service.description}</p>
      <AddonHints service={squareService} testId={`addon-hints-${service.id}`} />
      <button
        type="button"
        onClick={() => onBook(service.name)}
        data-testid={`book-now-${service.id}`}
        className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-display uppercase tracking-wider font-bold text-sm w-fit"
      >
        <span>Book Now</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
      </button>
    </div>
  </div>
);

export const Services = () => {
  const [bookingService, setBookingService] = useState(null);
  const { services: squareServices } = useSquareServices();
  const matchSquare = (s) => findService(squareServices, s.bookingName || s.name);

  const featured = services.filter((s) => s.featured);
  const standard = services.filter((s) => !s.featured);

  return (
    <section id="services" className="py-24 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <span className="font-display uppercase tracking-[0.25em] text-sm font-semibold text-red-600">What We Do</span>
          <h2 className="font-display italic uppercase text-4xl md:text-5xl font-extrabold text-neutral-950 mt-2 mb-4">Our Services</h2>
          <p className="text-neutral-600 text-base md:text-lg">
            Professional mobile detailing designed to exceed your expectations.
          </p>
        </div>

        {featured.length > 0 && (
          <div className="mb-12 space-y-8" data-testid="featured-services">
            {featured.map((service) => <FeaturedCard key={service.id} service={service} squareService={matchSquare(service)} onBook={setBookingService} />)}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {standard.map((service) => (
            <ServiceCard key={service.id} service={service} squareService={matchSquare(service)} onBook={setBookingService} />
          ))}
        </div>
      </div>

      <BookingFlow
        open={!!bookingService}
        onClose={() => setBookingService(null)}
        serviceName={bookingService}
      />
    </section>
  );
};
